import { debug, setMultipleAttributes, setTargetingParams } from '../utils.js';
import {
    resolveMonetizationInstance,
    DEFAULT_AD_CHOICES_PRODUCT_ID,
} from '../monetization/index.js';
import { getIdentityObject, resolveIdentityInstanceShared } from '../schibsted-account/index.js';

function getImmediateUserId(window, config) {
    if (typeof config.userId === 'number') {
        return config.userId;
    }

    const globalUserId = window?._sp_?.config?.userId;
    if (typeof globalUserId === 'number') {
        return globalUserId;
    }

    const schUserId = window?.schIdentity?._session?.userId;
    if (typeof schUserId === 'number') {
        return schUserId;
    }

    const legacyUserId = getIdentityObject(window, config)?._session?.userId;
    if (typeof legacyUserId === 'number') {
        return legacyUserId;
    }

    return undefined;
}

async function resolveUserIdForAdChoices(window, config) {
    const immediate = getImmediateUserId(window, config);
    if (typeof immediate === 'number') return immediate;

    try {
        // This will only resolve the Identity instance, but not populate the internal _session state
        const instance = await resolveIdentityInstanceShared(window, config);
        return await instance.getUserId();
    } catch (e) {
        debug('[CMP] resolveIdentityInstance failed, timed out or there is no user session available', e);
    }

    return undefined;
}

export async function checkAdChoicesAccess(window, config) {
    if (!config.enableAdChoicesFeature) {
        return false;
    }

    try {
        // Start waiting for monetization early so we don't miss a schMonetization:ready event while resolving identity/userId.
        const monetizationPromise = resolveMonetizationInstance(window, config);
        const userId = await resolveUserIdForAdChoices(window, config);

        if (typeof userId !== 'number') {
            // Avoid an unhandled rejection if monetizationPromise later rejects.
            monetizationPromise.catch(() => undefined);
            debug('[CMP] AdChoices skipped - no userId available');
            return false;
        }

        const monetization = await monetizationPromise;

        const productId = config.adChoicesProductId || DEFAULT_AD_CHOICES_PRODUCT_ID;
        const access = await monetization.hasAccess([productId], userId);

        if (access?.entitled === true) {
            setTargetingParams(config, 'enableAdChoices', true);
            // mark on config that this user is an AdChoices subscriber
            try {
              config.__sp_isAdChoicesSubscriber = true;
              debug('[CMP] AdChoices granted - user is entitled');
            } catch (e) {
                debug('[CMP] Unable to set __sp_isAdChoicesSubscriber on config', e);
            }
            return true;
        }

        debug('[CMP] AdChoices skipped - user is not entitled');

    } catch (e) {
        debug('[CMP] Monetization check failed', e);
    }

    return false;
}

export async function postRejectAll(window) {
    if (window.__sp_adChoicesPostRejectAllDone) {
        return;
    }

    // This function assumes window.__tcfapi is available as it's called within the TCF integration flow.
    if (typeof window.__tcfapi !== 'function') {
        debug('[CMP] AdChoices postRejectAll skipped - __tcfapi not found');
        return;
    }

    await new Promise((resolve) => {
        window.__tcfapi('postRejectAll', 2, (response, success) => {
            if (success) {
                window.__sp_adChoicesPostRejectAllDone = true;
                debug('[CMP] AdChoices postRejectAll success', response);
            } else {
                debug('[CMP] AdChoices postRejectAll failure', response);
            }
            resolve();
        });
    });
}

export function loadMessagingScript(config, _document) {
    const wrapperMessage = `${config.baseEndpoint}/unified/wrapperMessagingWithoutDetection.js`;
    const allScripts = _document.getElementsByTagName('script');
    const curScript = allScripts[allScripts.length - 1];

    // preload wrapperMessagingWithoutDetection script
    const preloadLink = _document.createElement('link');
    setMultipleAttributes(preloadLink, {
        rel: 'preload',
        as: 'script',
        crossorigin: 'crossorigin',
        href: wrapperMessage,
    });
    _document.head.appendChild(preloadLink);

    // append wrapperMessagingWithoutDetection script
    const commonScript = _document.createElement('script');
    setMultipleAttributes(commonScript, {
        async: true,
        src: wrapperMessage,
        onload: () => dispatchCmpLoadedEvent(),
    });
    curScript.insertAdjacentElement('afterend', commonScript);
}

export function dispatchCmpLoadedEvent() {
    debug('dispatching cmp:loaded event');
    document.dispatchEvent(new Event('cmp:loaded'));
}
export function dispatchCmpInitEvent() {
    debug('dispatching cmp:ready event');
    document.dispatchEvent(new Event('cmp:ready'));
}

export function loadTcfApi(_document) {
    const tcfScript = _document.createElement('script');
    // more details in Sourcepoint docs: https://docs.sourcepoint.com/hc/en-us/articles/4405412395667-CMP-web-implementation#:~:text=The%20first%20part,failure%20of%20service
    tcfScript.innerHTML =
        '"use strict";function _typeof(t){return(_typeof="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t})(t)}!function(){var t=function(){var t,e,o=[],n=window,r=n;for(;r;){try{if(r.frames.__tcfapiLocator){t=r;break}}catch(t){}if(r===n.top)break;r=r.parent}t||(!function t(){var e=n.document,o=!!n.frames.__tcfapiLocator;if(!o)if(e.body){var r=e.createElement("iframe");r.style.cssText="display:none",r.name="__tcfapiLocator",e.body.appendChild(r)}else setTimeout(t,5);return!o}(),n.__tcfapi=function(){for(var t=arguments.length,n=new Array(t),r=0;r<t;r++)n[r]=arguments[r];if(!n.length)return o;"setGdprApplies"===n[0]?n.length>3&&2===parseInt(n[1],10)&&"boolean"==typeof n[3]&&(e=n[3],"function"==typeof n[2]&&n[2]("set",!0)):"ping"===n[0]?"function"==typeof n[2]&&n[2]({gdprApplies:e,cmpLoaded:!1,cmpStatus:"stub"}):o.push(n)},n.addEventListener("message",(function(t){var e="string"==typeof t.data,o={};if(e)try{o=JSON.parse(t.data)}catch(t){}else o=t.data;var n="object"===_typeof(o)&&null!==o?o.__tcfapiCall:null;n&&window.__tcfapi(n.command,n.version,(function(o,r){var a={__tcfapiReturn:{returnValue:o,success:r,callId:n.callId}};t&&t.source&&t.source.postMessage&&t.source.postMessage(e?JSON.stringify(a):a,"*")}),n.parameter)}),!1))};"undefined"!=typeof module?module.exports=t:t()}();';
    _document.head.appendChild(tcfScript);
}

export function loadExternalToolsScript(_document, config) {
    if (config.disableSentry) return;
    const newScript = _document.createElement('script');
    setMultipleAttributes(newScript, {
        async: true,
        type: 'module',
        src: 'https://static.privacy.schibsted.com/dist/index.js',
        fetchPriority: 'low',
    });
    _document.head.appendChild(newScript);
}
